import React, { useState, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Upload, Download, FileSpreadsheet, CheckCircle, XCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';

interface ImportarInventarioDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  clienteId?: string;
  clienteNombre?: string;
  sedeId?: string;
  sedeNombre?: string;
}

interface InventarioRow {
  cliente_nombre: string;
  sistema_tipo: string;
  sistema_categoria: string;
  sistema_descripcion?: string;
  sistema_ubicacion?: string;
  elemento_nombre: string;
  elemento_tipo: string;
  elemento_estado?: string;
  marca?: string;
  modelo?: string;
  zona?: string;
  mac_serie?: string;
  fecha_instalacion?: string;
}

interface ParsedRow {
  data: InventarioRow;
  errors: string[];
  rowIndex: number;
}

type ImportStep = 'upload' | 'preview' | 'importing' | 'done';

const TEMPLATE_COLUMNS = [
  'cliente_nombre', 'sistema_tipo', 'sistema_categoria', 'sistema_descripcion', 'sistema_ubicacion',
  'elemento_nombre', 'elemento_tipo', 'elemento_estado', 'marca', 'modelo', 'zona', 'mac_serie', 'fecha_instalacion',
];

const COLUMN_LABELS: Record<string, string> = {
  cliente_nombre: 'Cliente *',
  sistema_tipo: 'Tipo Sistema *',
  sistema_categoria: 'Categoría *',
  sistema_descripcion: 'Descripción Sistema',
  sistema_ubicacion: 'Ubicación Sistema',
  elemento_nombre: 'Nombre Elemento *',
  elemento_tipo: 'Tipo Elemento *',
  elemento_estado: 'Estado',
  marca: 'Marca',
  modelo: 'Modelo',
  zona: 'Zona',
  mac_serie: 'MAC/Serie',
  fecha_instalacion: 'Fecha Instalación',
};

const BATCH_SIZE = 50;

export const ImportarInventarioDialog: React.FC<ImportarInventarioDialogProps> = ({
  open, onOpenChange, onSuccess, clienteId, clienteNombre, sedeId, sedeNombre
}) => {
  const { perfil } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<ImportStep>('upload');
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [progress, setProgress] = useState(0);
  const [importResult, setImportResult] = useState({ created: 0, errors: 0 });

  const reset = useCallback(() => {
    setStep('upload');
    setParsedRows([]);
    setProgress(0);
    setImportResult({ created: 0, errors: 0 });
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleOpenChange = (value: boolean) => {
    if (!value) reset();
    onOpenChange(value);
  };

  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      TEMPLATE_COLUMNS.map(c => COLUMN_LABELS[c] || c),
      [clienteNombre || 'Bar El Rincón', 'intrusión', 'seguridad', 'Sistema alarma principal', 'Planta baja',
       'Central alarma', 'Central', 'operativo', 'DSC', 'PowerSeries Neo', 'Zona 1', 'SN-12345', '2024-01-15'],
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sistemas y Elementos');
    ws['!cols'] = TEMPLATE_COLUMNS.map(() => ({ wch: 18 }));
    XLSX.writeFile(wb, 'plantilla_sistemas_elementos.xlsx');
  };

  const parseFile = async (file: File) => {
    if (!perfil?.empresa_id) return;
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json<Record<string, any>>(sheet, { defval: '' });

        if (jsonData.length === 0) {
          toast({ title: 'Archivo vacío', description: 'El archivo no contiene datos.', variant: 'destructive' });
          return;
        }

        const reverseMap: Record<string, string> = {};
        for (const key of TEMPLATE_COLUMNS) {
          reverseMap[(COLUMN_LABELS[key] || key).toLowerCase().replace(/\s*\*\s*/g, '').trim()] = key;
          reverseMap[key.toLowerCase()] = key;
        }

        const rows: ParsedRow[] = jsonData.map((row, idx) => {
          const mapped: Record<string, string> = {};
          for (const [header, value] of Object.entries(row)) {
            const normalizedHeader = header.toLowerCase().replace(/\s*\*\s*/g, '').trim();
            const matchedKey = reverseMap[normalizedHeader];
            if (matchedKey) mapped[matchedKey] = String(value || '').trim();
          }

          const errors: string[] = [];
          if (!clienteId && !mapped.cliente_nombre) errors.push('Falta cliente');
          if (!mapped.sistema_tipo) errors.push('Falta tipo sistema');
          if (!mapped.sistema_categoria) errors.push('Falta categoría');
          if (!mapped.elemento_nombre) errors.push('Falta nombre elemento');
          if (!mapped.elemento_tipo) errors.push('Falta tipo elemento');

          return { data: mapped as unknown as InventarioRow, errors, rowIndex: idx + 2 };
        });

        setParsedRows(rows);
        setStep('preview');
      } catch {
        toast({ title: 'Error al leer archivo', description: 'No se pudo parsear el archivo.', variant: 'destructive' });
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) parseFile(file);
  };

  const validRows = parsedRows.filter(r => r.errors.length === 0);
  const invalidRows = parsedRows.filter(r => r.errors.length > 0);

  const handleImport = async () => {
    if (!perfil?.empresa_id) return;
    setStep('importing');
    let created = 0;
    let errors = 0;

    // Load existing clients to map names to IDs
    const { data: clientes } = await supabase
      .from('clientes')
      .select('id, nombre')
      .eq('empresa_id', perfil.empresa_id);
    const clienteMap = new Map((clientes || []).map(c => [c.nombre.toLowerCase().trim(), c.id]));

    // Group rows by sistema (client + type + category + description)
    const sistemaGroups = new Map<string, { clienteDbId: string; tipo: string; categoria: string; descripcion: string; ubicacion: string; elementos: InventarioRow[] }>();

    for (const row of validRows) {
      const cId = clienteId || clienteMap.get(row.data.cliente_nombre.toLowerCase().trim());
      if (!cId) { errors++; continue; }

      const key = `${cId}|${row.data.sistema_tipo}|${row.data.sistema_categoria}|${row.data.sistema_descripcion || ''}`;
      if (!sistemaGroups.has(key)) {
        sistemaGroups.set(key, {
          clienteDbId: cId,
          tipo: row.data.sistema_tipo,
          categoria: row.data.sistema_categoria,
          descripcion: row.data.sistema_descripcion || '',
          ubicacion: row.data.sistema_ubicacion || '',
          elementos: [],
        });
      }
      sistemaGroups.get(key)!.elementos.push(row.data);
    }

    const totalOps = validRows.length;

    for (const [, group] of sistemaGroups) {
      // Create or find sistema (filtrado también por sede si está definida)
      let existingQuery = supabase
        .from('sistemas')
        .select('id')
        .eq('cliente_id', group.clienteDbId)
        .eq('tipo', group.tipo)
        .eq('categoria', group.categoria)
        .eq('empresa_id', perfil.empresa_id);
      if (sedeId) existingQuery = existingQuery.eq('sede_id', sedeId);
      const { data: existingSistema } = await existingQuery.limit(1).single();

      let sistemaId = existingSistema?.id;
      if (!sistemaId) {
        const { data: newSistema, error } = await supabase.from('sistemas').insert({
          cliente_id: group.clienteDbId,
          tipo: group.tipo,
          categoria: group.categoria,
          descripcion: group.descripcion || null,
          ubicacion: group.ubicacion || null,
          empresa_id: perfil.empresa_id,
          sede_id: sedeId || null,
        } as any).select('id').single();
        if (error) { errors += group.elementos.length; continue; }
        sistemaId = newSistema?.id;
      }

      if (!sistemaId) { errors += group.elementos.length; continue; }

      // Batch insert elements
      const elementosData = group.elementos.map(el => ({
        sistema_id: sistemaId!,
        nombre: el.elemento_nombre,
        tipo: el.elemento_tipo,
        estado: el.elemento_estado || 'operativo',
        marca: el.marca || null,
        modelo: el.modelo || null,
        zona: el.zona || null,
        mac_serie: el.mac_serie || null,
        fecha_instalacion: el.fecha_instalacion || null,
      }));

      for (let i = 0; i < elementosData.length; i += BATCH_SIZE) {
        const batch = elementosData.slice(i, i + BATCH_SIZE);
        const { error } = await supabase.from('elementos').insert(batch as any);
        if (error) { errors += batch.length; } else { created += batch.length; }
        setProgress(Math.round(((created + errors) / totalOps) * 100));
      }
    }

    setImportResult({ created, errors: errors + invalidRows.length });
    setStep('done');
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[750px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Importar Inventario
          </DialogTitle>
          <DialogDescription>
            Importa sistemas y elementos desde un archivo Excel o CSV.
            {clienteNombre && <span className="font-medium"> Cliente: {clienteNombre}</span>}
          </DialogDescription>
        </DialogHeader>

        {step === 'upload' && (
          <div className="space-y-6 py-4">
            <div className="flex justify-center">
              <Button variant="outline" onClick={downloadTemplate} className="gap-2">
                <Download className="h-4 w-4" />
                Descargar plantilla Excel
              </Button>
            </div>
            <div
              className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
              <p className="font-medium">Haz clic para seleccionar un archivo</p>
              <p className="text-sm text-muted-foreground mt-1">Formatos: .xlsx, .xls, .csv</p>
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleFileChange} />
            </div>
          </div>
        )}

        {step === 'preview' && (
          <div className="space-y-4 flex-1 min-h-0">
            <div className="flex items-center gap-3 flex-wrap">
              {validRows.length > 0 && (
                <Badge variant="default" className="gap-1"><CheckCircle className="h-3 w-3" />{validRows.length} válidos</Badge>
              )}
              {invalidRows.length > 0 && (
                <Badge variant="destructive" className="gap-1"><XCircle className="h-3 w-3" />{invalidRows.length} con errores</Badge>
              )}
              <span className="text-sm text-muted-foreground">Total: {parsedRows.length} filas</span>
            </div>
            <ScrollArea className="h-[350px] border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Sistema</TableHead>
                    <TableHead>Elemento</TableHead>
                    <TableHead>Marca/Modelo</TableHead>
                    <TableHead className="w-28">Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parsedRows.map((row) => (
                    <TableRow key={row.rowIndex} className={row.errors.length > 0 ? 'bg-destructive/10' : ''}>
                      <TableCell className="text-muted-foreground text-xs">{row.rowIndex}</TableCell>
                      <TableCell className="text-sm">{row.data.cliente_nombre || clienteNombre || '—'}</TableCell>
                      <TableCell className="text-sm">{row.data.sistema_tipo} ({row.data.sistema_categoria})</TableCell>
                      <TableCell className="font-medium text-sm">{row.data.elemento_nombre || '—'}</TableCell>
                      <TableCell className="text-sm">{[row.data.marca, row.data.modelo].filter(Boolean).join(' ') || '—'}</TableCell>
                      <TableCell>
                        {row.errors.length > 0 ? (
                          <span className="text-destructive text-xs flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />{row.errors[0]}
                          </span>
                        ) : (
                          <Badge variant="outline" className="text-xs gap-1 border-green-500 text-green-600">
                            <CheckCircle className="h-3 w-3" />OK
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
            <div className="flex justify-between items-center pt-2">
              <Button variant="outline" onClick={reset}>Cancelar</Button>
              <Button onClick={handleImport} disabled={validRows.length === 0} className="gap-2">
                <Upload className="h-4 w-4" />Importar {validRows.length} elementos
              </Button>
            </div>
          </div>
        )}

        {step === 'importing' && (
          <div className="py-8 space-y-4">
            <div className="flex items-center justify-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin" /><span className="font-medium">Importando inventario...</span>
            </div>
            <Progress value={progress} className="h-2" />
            <p className="text-center text-sm text-muted-foreground">{progress}%</p>
          </div>
        )}

        {step === 'done' && (
          <div className="py-8 space-y-4 text-center">
            <CheckCircle className="h-12 w-12 mx-auto text-green-500" />
            <h3 className="text-lg font-semibold">Importación completada</h3>
            <div className="flex justify-center gap-4 flex-wrap">
              {importResult.created > 0 && (
                <Badge variant="default" className="text-sm gap-1"><CheckCircle className="h-3 w-3" />{importResult.created} creados</Badge>
              )}
              {importResult.errors > 0 && (
                <Badge variant="destructive" className="text-sm gap-1"><XCircle className="h-3 w-3" />{importResult.errors} con errores</Badge>
              )}
            </div>
            <Button onClick={() => { handleOpenChange(false); onSuccess(); }} className="mt-4">Cerrar</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
